import { Fragment } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useMyTenants } from '../hooks';
import { Icon } from '../shared/Icon';
import type { IconName } from '../shared/icons';
import type { ApiError } from '../api';

/**
 * Каркас приложения внутри тенанта: сайдбар кабинета плюс рабочая область.
 * Тенант берётся из URL, а не из состояния, — тогда ссылка на счёт открывается в нужном тенанте
 * без предварительного выбора.
 */
export function AppLayout() {
  const { tenantId = '' } = useParams();
  const navigate = useNavigate();
  const me = useMyTenants();

  const current = me.data?.find((t) => t.tenant.id === tenantId);

  if (me.isLoading) return <div className="state">Загрузка…</div>;
  if (me.isError) {
    // 401 означает переход ко входу, а не поломку.
    return (
      <div className="state">
        {(me.error as ApiError).status === 401 ? 'Переходим ко входу…' : 'Сервис биллинга недоступен'}
      </div>
    );
  }
  if (!current) {
    return (
      <div className="entry-page">
        <div className="entry-wrap">
          <div className="card">
            <div className="state-title">Тенант недоступен</div>
            <p className="muted">Возможно, вас исключили из него или ссылка ведёт в чужой тенант.</p>
            <div className="state-actions">
              <button className="btn btn-primary" onClick={() => navigate('/')}>
                К списку тенантов
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const nav: Array<{
    section: string;
    items: Array<{ to: string; label: string; icon: IconName; end?: boolean }>;
  }> = [
    {
      section: 'Работа',
      items: [
        { to: `/t/${tenantId}/dashboard`, label: 'Сводка', icon: 'dashboard' },
        { to: `/t/${tenantId}/invoices`, label: 'Счета', icon: 'contract', end: true },
        { to: `/t/${tenantId}/usage`, label: 'Потребление', icon: 'activity' },
      ],
    },
    {
      section: 'Настройка',
      items: [
        { to: `/t/${tenantId}/customers`, label: 'Абоненты', icon: 'users' },
        { to: `/t/${tenantId}/meters`, label: 'Счётчики', icon: 'terminal' },
        { to: `/t/${tenantId}/plans`, label: 'Тарифы', icon: 'card' },
        { to: `/t/${tenantId}/contracts`, label: 'Договоры', icon: 'handshake' },
      ],
    },
    {
      section: 'Тенант',
      items: [{ to: `/t/${tenantId}/settings`, label: 'Настройки', icon: 'settings' }],
    },
  ];

  const logout = async () => {
    const res = await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
    const body = (await res.json().catch(() => ({}))) as { redirectTo?: string };
    window.location.href = body.redirectTo ?? '/';
  };

  const role = current.role ?? (current.platformAdmin ? 'администратор платформы' : '');

  return (
    <div className="app-layout">
      <nav className="sidebar">
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon">
            <Icon name="activity" size={18} />
          </div>
          <div className="sidebar-logo-text">Биллинг</div>
        </div>

        {nav.map((group) => (
          <Fragment key={group.section}>
            <div className="sidebar-section">{group.section}</div>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              >
                <Icon name={item.icon} />
                {item.label}
              </NavLink>
            ))}
          </Fragment>
        ))}

        <div className="sidebar-footer">
          {/* Переключатель тенантов: у оператора их обычно несколько. */}
          {(me.data?.length ?? 0) > 1 && (
            <select
              className="input"
              value={tenantId}
              onChange={(event) => navigate(`/t/${event.target.value}/dashboard`)}
              aria-label="Тенант"
            >
              {me.data!.map((t) => (
                <option key={t.tenant.id} value={t.tenant.id}>
                  {t.tenant.name}
                </option>
              ))}
            </select>
          )}
          <div className="sidebar-user">
            <div className="user-avatar">{initials(current.tenant.name)}</div>
            <div style={{ minWidth: 0 }}>
              <div className="user-name">{current.tenant.name}</div>
              <div className="user-id">{roleLabel(role)}</div>
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={logout}>
            <Icon name="logout" />
            Выйти
          </button>
        </div>
      </nav>

      <div className="main-area">
        <main className="page-content">
          {current.tenant.status === 'SUSPENDED' && (
            <div className="info-banner amber">
              Тенант приостановлен: данные видны, но выставление счетов и приём оплат остановлены.
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

const roleLabel = (role: string): string =>
  ({ OWNER: 'владелец', ADMIN: 'администратор', VIEWER: 'наблюдатель' })[role] ?? role;
